"use client";

import { formatShortDateWithPreferences } from "@workspace/shared-utils";
import type { FestivalDetails } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";

import { StarRating } from "@/components/shared";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";

// Using camelCase to match tRPC data
interface UserFestivalReview {
	id?: string;
	festivalId: string;
	userId: string;
	overallRating: number;
	vibeRating?: number;
	productionRating?: number;
	logisticsRating?: number;
	foodRating?: number;
	content: string | null;
	updatedAt?: string;
}

interface ReadOnlyStarsProps {
	rating: number;
	className?: string;
}

const ReadOnlyStars = ({ rating, className }: ReadOnlyStarsProps) => {
	return <StarRating rating={rating} size="md" className={className} />;
};

interface CategoryRatingDisplayProps {
	label: string;
	description?: string;
	rating: number;
}

const CategoryRatingDisplay = ({
	label,
	description,
	rating,
}: CategoryRatingDisplayProps) => {
	// Don't show categories with no rating or unrated (values <= 0)
	if (!rating || rating <= 0) return null;

	return (
		<div className="space-y-1">
			<Label>{label}</Label>
			{description && (
				<p className="text-muted-foreground text-xs">{description}</p>
			)}
			<ReadOnlyStars rating={rating} />
		</div>
	);
};

interface FestivalReviewModalReadOnlyProps {
	isOpen: boolean;
	festival: FestivalDetails | null;
	userReview: UserFestivalReview | null;
	onClose: () => void;
}

// Rating categories configuration
const RATING_CATEGORIES = [
	{
		id: "vibeRating",
		label: "Vibe",
		description: "Overall atmosphere, energy, and crowd",
	},
	{
		id: "productionRating",
		label: "Event Production",
		description: "Sound quality, visuals, and stage design",
	},
	{
		id: "logisticsRating",
		label: "Logistics & Facilities",
		description: "Parking, transportation, lines and bathroom",
	},
	{
		id: "foodRating",
		label: "Food, Drinks & Activities",
		description: "Overall value, quality and variety",
	},
];

export function FestivalReviewModalReadOnly({
	isOpen,
	festival,
	userReview,
	onClose,
}: FestivalReviewModalReadOnlyProps) {
	const userPreferences = useUserPreferences();

	if (!festival || !userReview) {
		return null; // Don't render if festival data or review is missing
	}

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md md:max-w-lg">
				<DialogTitle className="sr-only">Festival Review</DialogTitle>
				<DialogDescription className="sr-only">
					View festival review and rating details
				</DialogDescription>
				<DialogHeader className="px-6 pt-6 pb-0">
					<h2 className="font-semibold leading-none tracking-tight">
						{festival.name} Review
					</h2>
					{userReview.updatedAt && (
						<p className="text-muted-foreground text-sm">
							Last updated:{" "}
							{formatShortDateWithPreferences(
								userReview.updatedAt,
								userPreferences,
							)}
						</p>
					)}
				</DialogHeader>
				<div className="space-y-4 px-6">
					{/* Overall Rating */}
					<div className="space-y-2 border-b pb-4">
						<Label>Overall Rating</Label>
						<ReadOnlyStars rating={userReview.overallRating} />
					</div>

					{/* Category Ratings */}
					<div className="space-y-4">
						{RATING_CATEGORIES.map((category) => {
							const rating = userReview[
								category.id as keyof UserFestivalReview
							] as number | undefined;
							return (
								<CategoryRatingDisplay
									key={category.id}
									label={category.label}
									description={category.description}
									rating={rating || 0}
								/>
							);
						})}
					</div>

					{/* Review text */}
					{userReview.content && (
						<div className="rounded-lg bg-muted p-3 text-sm">
							{userReview.content}
						</div>
					)}
				</div>
				<DialogFooter className="px-6 pt-0 pb-6">
					<div className="flex w-full items-center justify-end">
						<Button onClick={onClose}>Close</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
